import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useAuth } from 'oidc-react';
import moment from 'moment';
import ApiService from '../../../services/ApiService';

const DetailItemsTable = ({ data = [], statusHeader, onActionSet }) => {
  const { userData } = useAuth();
  const [userEmail, setUserEmail] = useState(userData?.profile?.email);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [filteredData, setFilteredData] = useState(data);
  const [searchQuery, setSearchQuery] = useState('');

  const [pic, setPic] = useState(
    [...new Set(data.map((item) => item.picDisplayName))].map((name) => ({
      name,
      value: name,
    }))
  );

  const [status, setStatus] = useState([
    { name: 'Open', value: 'Open' },
    { name: 'Decline', value: 'Decline' },
    { name: 'Accepted', value: 'Accepted' },
    { name: 'Extend', value: 'Extend' },
    { name: 'Review', value: 'Review' },
    { name: 'Return to PIC', value: 'Return to PIC' },
    { name: 'Closed', value: 'Closed' },
  ]);

  const [priority, setPriority] = useState([
    { name: 'Low', value: 1 },
    { name: 'Medium', value: 2 },
    { name: 'High', value: 3 },
  ]);

  const getMenuItems = (row) => {
    return row.action.map((action) => (
      <MenuItem
        key={action}
        onClick={(event) => handleMenuItemClick(event, action)}
      >
        {action}
      </MenuItem>
    ));
  };

  const handleButtonClick = (event, params) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(params.row.id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleMenuItemClick = (event, action) => {
    onActionSet(action, selectedRowId);
  };

  const columns = [
    {
      sortable: false,
      field: 'pembahasan',
      headerName: 'Pembahasan',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>
            {params.value && params.value.length > 25
              ? `${params.value.substring(0, 40)}...`
              : params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      sortable: false,
      field: 'tindakLanjut',
      headerName: 'Tindak Lanjut',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>
            {params.value && params.value.length > 25
              ? `${params.value.substring(0, 40)}...`
              : params.value}
          </span>
        </Tooltip>
      ),
    },
    { sortable: false, field: 'picDisplayName', headerName: 'PIC', width: 200 },
    {
      sortable: false,
      field: 'reviewerDisplayName',
      headerName: 'Reviewer',
      width: 200,
    },
    {
      sortable: false,
      field: 'statusDetailName',
      headerName: 'Status',
      renderCell: (params) => {
        return (
          <div
            style={{
              borderRadius: '5px',
              padding: '8px 0',
              width: '100px',
              alignItems: 'center',
              textAlign: 'center',
              backgroundColor:
                params.value === 'Open'
                  ? '#ffd500'
                  : params.value === 'Accepted' || params.value === 'Extend'
                  ? '#ffffff'
                  : params.value === 'Decline' ||
                    params.value === 'Return to PIC'
                  ? '#fff700'
                  : params.value === 'Review'
                  ? '#0ff7f3'
                  : params.value === 'Closed'
                  ? '#05f535'
                  : '#ffd500',
            }}
          >
            <span
              style={{
                fontWeight: 'bold',
                color: '#030303',
              }}
            >
              {params.value}
            </span>
          </div>
        );
      },
    },
    {
      field: 'dueDateCreator',
      headerName: 'Due Date',
      width: 200,
      renderCell: (params) => {
        return (
          <div>
            {params.value === null
              ? '-'
              : moment(params.value).format('DD MMMM YYYY')}
          </div>
        );
      },
    },
    {
      field: 'dueDateExtend',
      headerName: 'Due Date Extend',
      width: 200,
      renderCell: (params) => {
        return (
          <div>
            {params.value === null
              ? '-'
              : moment(params.value).format('DD MMMM YYYY')}
          </div>
        );
      },
    },
    {
      sortable: false,
      field: 'priority',
      headerName: 'Priority',
      renderCell: (params) => {
        const priorityValue = params.value;
        const priorityItem = priority.find(
          (item) => item.value === priorityValue
        );

        if (priorityItem) {
          return (
            <div
              style={{
                backgroundColor:
                  priorityItem.name === 'Low'
                    ? '#369120'
                    : priorityItem.name === 'Medium'
                    ? '#FFFF00'
                    : priorityItem.name === 'High'
                    ? '#BE0000'
                    : '#98FB98',
                borderRadius: '5px',
                padding: '8px 0',
                width: '100px',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <span
                style={{
                  fontWeight: 'bold',
                  color: '#030303',
                }}
              >
                {priorityItem.name}
              </span>
            </div>
          );
        }

        return null;
      },
    },
    {
      sortable: false,
      field: 'tingkatKesulitan',
      headerName: 'Tingkat Kesulitan',
      width: 150,
      renderCell: (val) => {
        return (
          <div
            style={{
              backgroundColor:
                val.value <= 1
                  ? '#98FB98'
                  : val.value === 2
                  ? '#FFFF00'
                  : val.value >= 3
                  ? '#FF0000'
                  : '#98FB98',
              borderRadius: '5px',
              padding: '8px 0',
              width: '100px',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontWeight: 'bold',
                color: '#030303',
              }}
            >
              {val.value <= 1
                ? 'Mudah'
                : val.value === 2
                ? 'Medium'
                : val.value >= 3
                ? 'Sulit'
                : 'Undefined'}
            </span>
          </div>
        );
      },
    },
    {
      sortable: false,
      field: 'statusRemark',
      headerName: 'Status Remark',
      width: 300,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{params.value}</div>
      ),
    },
    {
      sortable: false,
      field: 'fileUploadOriginalName',
      headerName: 'Attachment',
      renderCell: (params) => {
        return (
          <div>
            <a
              onClick={async (e) =>
                await ApiService.viewFile(params.row.fileUploadUrl, e)
              }
              href="#"
              rel="noreferrer"
              style={{
                fontWeight: 'bold',
                color: '#6c8dff',
              }}
            >
              {params.row.fileUploadOriginalName}
            </a>
          </div>
        );
      },
    },
    {
      sortable: false,
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {params.row.action.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={(event) => handleButtonClick(event, params)}
            >
              Action
            </Button>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRowId === params.row.id}
            onClose={handleMenuClose}
          >
            {getMenuItems(params.row)}
          </Menu>
        </>
      ),
    },

    // { field: 'picJabatan', headerName: 'PIC Jabatan' },
    // { field: 'id', headerName: 'ID', width: 100 },
    // { field: 'picEmail', headerName: 'PIC', hide: true },
    // { field: 'reviewerEmail', headerName: 'Reviewer', hide: true },
    // { field: 'reviewerJabatan', headerName: 'Reviewer Jabatan', width: 200 },
    // { field: 'idStatusDetail', headerName: 'ID Status Detail', width: 150 },
    // { field: 'fileUploadUrl', headerName: 'File Upload URL', width: 200 },
    // { field: 'isStatus', headerName: 'Is Status', width: 100 },
  ];

  const handlePriorityChange = (e, value) => {
    const filteredRows = data.filter((row) => {
      const priorityName = priority.find(
        (item) => item.value === row.priority
      ).name;
      return String(priorityName)
        .toLowerCase()
        .includes(value ? value.name.toLowerCase() : '');
    });
    setFilteredData(filteredRows);
  };

  const handleStatusChange = (e, value) => {
    const filteredRows = data.filter((row) =>
      String(row.statusDetailName)
        .toLowerCase()
        .includes(value ? value.name.toLowerCase() : '')
    );
    setFilteredData(filteredRows);
  };

  const handlePicSearch = (e, value) => {
    const filteredRows = data.filter((row) =>
      String(row.picDisplayName)
        .toLowerCase()
        .includes(value ? value.name.toLowerCase() : '')
    );
    setFilteredData(filteredRows);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);

    const filteredRows = data.filter(
      (row) =>
        String(row.pembahasan).toLowerCase().includes(value.toLowerCase()) ||
        String(row.tindakLanjut).toLowerCase().includes(value.toLowerCase()) ||
        String(row.reviewerDisplayName)
          .toLowerCase()
          .includes(value.toLowerCase())
    );
    setFilteredData(filteredRows);
  };

  return (
    <Grid
      container
      spacing={2}
      marginBottom={3}
      justifyContent="space-around"
      alignItems="center"
    >
      <Grid
        item
        xs={10}
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={3}>
          <FormControl fullWidth>
            <Autocomplete
              name="PIC"
              onChange={handlePicSearch}
              required
              getOptionLabel={(option) => option.name}
              options={pic}
              renderInput={(params) => <TextField {...params} label="PIC" />}
              getOptionSelected={(option, value) => option.name === value.name}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <Autocomplete
              name="status"
              onChange={handleStatusChange}
              required
              getOptionLabel={(option) => option.name}
              options={status}
              renderInput={(params) => <TextField {...params} label="Status" />}
              getOptionSelected={(option, value) => option.name === value.name}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <Autocomplete
              name="priority"
              onChange={handlePriorityChange}
              required
              getOptionLabel={(option) => option.name}
              options={priority}
              renderInput={(params) => (
                <TextField {...params} label="Priority" />
              )}
              getOptionSelected={(option, value) => option.name === value.name}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <TextField
              label="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              sx={{ height: '56px', width: '100%' }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        item
        xs={2}
        justifyContent="center"
        alignItems="center"
        container
      ></Grid>
      <Grid item xs={12} style={{ width: '100%' }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          autoHeight
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableSelectionOnClick={true}
          disableColumnMenu={true}
        />
      </Grid>
    </Grid>
  );
};

export default DetailItemsTable;
